import React, {useState, useCallback, useMemo} from "react";
import Text from "../../2-component/Text";
import {ContentWrapper} from "./style";
import {IAddressDetails} from "@natomas-org/core";
import {InputLibrary} from "../../1-container/forms/library/inputs";
import {useAddress} from "../../../components/_shared/hooks/useAddress";
import {CreateLeadSchema} from "@natomas-org/villa-nexus-client";
import Form from "../../2-component/Form/form";
import {ButtonProps} from "../../2-component/Button/interface";


interface ConfirmCustomerContentProps {
  onSubmit: (values: any, addressDetails: IAddressDetails) => void;
  schema?: CreateLeadSchema;
  additionalButtons?: ButtonProps[];
}

const getInputs = (
  defaultFirst?: string,
  defaultLast?: string,
  defaultAddress?: IAddressDetails,
  setAddressDetails?: (address: IAddressDetails) => void
) => {
  return [
    {...InputLibrary.firstNameInput, row: 1, defaultValue: defaultFirst},
    {...InputLibrary.lastNameInput, row: 1, defaultValue: defaultLast},
    InputLibrary.phoneInput,
    {
      ...InputLibrary.addressInput,
      defaultValue: defaultAddress?.full_address,
      setObject: setAddressDetails,
    },
    InputLibrary.marketingConsentCheckbox,
  ];
};

const ConfirmCustomerContent = (props: ConfirmCustomerContentProps) => {
  const {schema, onSubmit, additionalButtons} = props;
  const {url} = useAddress();
  
  // Only use the URL address during account creation to avoid overriding Google selections with cached data
  const initialAddress = url ?? undefined;
  const [addressDetails, setAddressDetails] = useState<
    IAddressDetails | undefined
  >(initialAddress);

  const handleAddressChange = useCallback((address: IAddressDetails | null | undefined) => {
    console.log('🔄 Address change triggered:', address);
    setAddressDetails(address || undefined);
  }, []);

  // Check if address has sufficient detail for backend requirements
  const isAddressComplete = useMemo(() => {
    if (!addressDetails) {
      console.log('❌ No addressDetails');
      return false;
    }
    
    // Require at least city and state for a valid address
    // For very broad locations like "Oregon, USA", city will be empty
    const isComplete = !!(
      addressDetails.latitude && 
      addressDetails.longitude && 
      addressDetails.city && // Must have city
      addressDetails.state // Must have state
    );
    
    console.log('🔍 Address validation check:', {
      hasCity: !!addressDetails.city,
      hasState: !!addressDetails.state,
      city: addressDetails.city,
      state: addressDetails.state,
      isComplete
    });
    
    return isComplete;
  }, [addressDetails]);

  // Track when button state changes
  React.useEffect(() => {
    console.log('🔘 Button disabled state changed:', !isAddressComplete);
  }, [isAddressComplete]);

  const inputs = getInputs(
    schema?.["first_name"],
    schema?.["last_name"],
    initialAddress,
    handleAddressChange
  );

  return (
    <ContentWrapper>
      <Text
        text={
          "A free account lets you save your Villa design, see how it fits on your lot and get an instant quote."
        }
        style={{
          textAlign: "center",
          fontSize: "14px !important",
          lineHeight: "18px !important",
        }}
      />
      <Form
        key={"confirm-customer-form"}
        inputs={inputs}
        onSubmit={(values) => {
          if (isAddressComplete) {
            onSubmit?.(values, addressDetails!);
          } else {
            window.alert("Please enter a complete address with city and state details.");
          }
        }}
        buttons={[
          ...(additionalButtons ?? []),
          {
            id: "direct-customer-form-confirm",
            label: "Confirm",
            disabled: !isAddressComplete,
            type: "submit",
          },
        ]}
      />
    </ContentWrapper>
  );
};

export default ConfirmCustomerContent;
